import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    useReactTable,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel
} from '@tanstack/react-table';

import {
    IconSort,
    TableGroups,
    TableControl,
    ResultCount,
    DivColumnVisibility,
    DivContainerColumnRow,
    IconMenuTableControl,
    DivSearchDropdown,
    IconArrow,
    DivPageVisibility,
    ButtonChangePage,
    SelectShowElement,
    IconDetail,
    DivContainerDetail,
    DivContainerDetailContent,
    IconClose,
    DivExportCSV,
    DivSpinner,
    ButtonExportCSV,
    DivLoader,
    DetailOverlay,
    DivTableWrapper
} from './table.styled';

import { RenderNestedObject } from '../../utils/renderNestedObject';
import { exportSearchCSV } from '../../api/internal/usersInternalApi.js';

export default function Table({
    columns,
    data,
    colVisibility,
    onRowSelectionChange,
    serverPagination,
    onPageChange,
    onPageSizeChange,
    onSortingChange,
    filters,
    isPageLoading,
    columnsType
}) {

    const isServerMode = !!serverPagination;

    const [filtering, setFiltering] = useState('');
    const [sorting, setSorting] = useState([]);
    const [openMenuColumns, setOpenMenuColumns] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState(colVisibility);
    const [detail, setDetail] = useState({});
    const [openDetail, setOpenDetail] = useState(false);
    const [nestedDetail, setNestedDetail] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [isExporting, setIsExporting] = useState(false);

    const columnVisibilityRef = useRef();
    const detailPageRef = useRef();
    const nestedDetailPageRef = useRef();
    const buttonBackDetailPageRef = useRef();
    const buttonOpenDetailPageRef = useRef([]);

    const handleExportCSV = async () => {
        if (!filters || !isServerMode) return;

        setIsExporting(true);
        try {
            await exportSearchCSV(filters);
        } catch {
            alert('Erreur lors de l\'export');
        } finally {
            setIsExporting(false);
        }
    };

    const handleRowSelectionChange = useCallback((updater) => {
        const newSelection =
            typeof updater === 'function'
                ? updater(rowSelection)
                : updater;

        setRowSelection(newSelection);

        const selectedRows = Object.keys(newSelection)
            .filter(key => newSelection[key])
            .map(key => data[parseInt(key)])
            .filter(Boolean);

        onRowSelectionChange?.(selectedRows);
    }, [rowSelection, data, onRowSelectionChange]);

    const handleSortingChange = useCallback((updater) => {
        const newSorting =
            typeof updater === 'function'
                ? updater(sorting)
                : updater;

        setSorting(newSorting);

        if (isServerMode) {
            if (newSorting.length > 0) {
                onSortingChange?.(
                    newSorting[0].id,
                    newSorting[0].desc ? 'desc' : 'asc'
                );
            } else {
                onSortingChange?.('displayName', 'asc');
            }
        }
    }, [sorting, isServerMode, onSortingChange]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),

        manualPagination: isServerMode,
        manualSorting: isServerMode,
        pageCount: isServerMode ? serverPagination.totalPages : undefined,

        columnResizeMode: "onChange",

        onSortingChange: handleSortingChange,
        onGlobalFilterChange: setFiltering,
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: handleRowSelectionChange,

        state: {
            sorting,
            globalFilter: filtering,
            columnVisibility,
            rowSelection,
        },

        defaultColumn: { minSize: 50 },
        enableRowSelection: true,
        enableMultiRowSelection: true,
    });

    const currentPage = isServerMode
        ? serverPagination.page
        : table.getState().pagination.pageIndex + 1;

    const totalPages = isServerMode
        ? serverPagination.totalPages
        : table.getPageCount();

    const totalCount = isServerMode
        ? serverPagination.totalCount
        : data.length;

    const pageSize = isServerMode
        ? serverPagination.limit
        : table.getState().pagination.pageSize;

    const canPrev = isServerMode
        ? serverPagination.hasPrevPage
        : table.getCanPreviousPage();

    const canNext = isServerMode
        ? serverPagination.hasNextPage
        : table.getCanNextPage();

    const prevPage = () => {
        if (isServerMode) {
            onPageChange(serverPagination.page - 1);
        } else {
            table.previousPage();
        }
    };

    const nextPage = () => {
        if (isServerMode) {
            onPageChange(serverPagination.page + 1);
        } else {
            table.nextPage();
        }
    };

    const changePageSize = (size) => {
        if (isServerMode) {
            onPageSizeChange(size);
        } else {
            table.setPageSize(size);
        }
    };

    useEffect(() => {
        const closeDetail = (e) => {
            const clickedInsideDetail = detailPageRef.current && e.composedPath().includes(detailPageRef.current);
            const clickedInsideNestedDetail = nestedDetail && nestedDetailPageRef.current && e.composedPath().includes(nestedDetailPageRef.current);
            const clickedOnOpenDetailButton = buttonOpenDetailPageRef.current.some(btn => e.composedPath().includes(btn));
            const clickedOnBackButton = buttonBackDetailPageRef.current && e.composedPath().includes(buttonBackDetailPageRef.current);
            const clickedOnColumnVisibility = columnVisibilityRef.current && e.composedPath().includes(columnVisibilityRef.current);

            if (!clickedInsideDetail && !clickedInsideNestedDetail && !clickedOnOpenDetailButton) {
                setOpenDetail(false);
                setNestedDetail(null);
            }

            if (clickedOnBackButton) {
                setNestedDetail(null);
                setOpenDetail(true);
            }

            if (!clickedOnColumnVisibility) {
                setOpenMenuColumns(false);
            }
        };

        document.addEventListener('click', closeDetail);
        return () => document.removeEventListener('click', closeDetail);
    }, [nestedDetail]);

    const getSortOrder = (isSorted, header) => {
        if (isSorted && sorting.length > 0) {
            return sorting[0].desc
                ? <IconSort className='bx bx-sort-down' onClick={header.column.getToggleSortingHandler()} />
                : <IconSort className='bx bx-sort-up' onClick={header.column.getToggleSortingHandler()} />;
        }
        return <IconSort className='bx bx-sort-alt-2' onClick={header.column.getToggleSortingHandler()} />;
    };

    const detailPage = (item) => {
        setDetail({ ...item });
        setOpenDetail(true);
    };

    const nestedDetailPage = (item) => {
        setNestedDetail(item);
        setOpenDetail(false);
    };

    return (
        <>
            {openDetail && (
                <>
                    <DetailOverlay onClick={() => setOpenDetail(false)} />
                    <DivContainerDetail ref={detailPageRef}>
                        <DivContainerDetailContent>
                            <IconClose className='bx bx-x' onClick={() => setOpenDetail(false)} />
                            <RenderNestedObject
                                data={detail}
                                exclude={['_id', '__v', 'profilePicture']}
                                models={undefined}
                                onClickDetail={nestedDetailPage}
                                primaryKey={undefined}
                                diffPageRender={true}
                                differences={undefined}
                            />
                        </DivContainerDetailContent>
                    </DivContainerDetail>
                </>
            )}

            {nestedDetail && (
                <DivContainerDetail ref={nestedDetailPageRef} className="nested-detail">
                    <DivContainerDetailContent>
                        <IconClose ref={buttonBackDetailPageRef} className='bx bx-arrow-back' />
                        <RenderNestedObject
                            data={nestedDetail}
                            exclude={['_id', '__v', 'profilePicture']}
                            models={undefined}
                            onClickDetail={nestedDetailPage}
                            primaryKey={undefined}
                            diffPageRender={true}
                            differences={undefined}
                        />
                    </DivContainerDetailContent>
                </DivContainerDetail>
            )}

            <TableControl>
                <ResultCount>
                    <strong>{totalCount}</strong>
                    {totalCount === 1 ? 'résultat' : 'résultats'}
                    {columnsType ? ` · ${columnsType}` : null}
                </ResultCount>
                {isServerMode && (
                    <DivExportCSV>
                        <ButtonExportCSV onClick={handleExportCSV} disabled={isExporting}>
                            <i className={isExporting ? 'bx bx-loader-alt bx-spin' : 'bx bxs-download'} />
                        </ButtonExportCSV>
                    </DivExportCSV>
                )}
                <DivColumnVisibility ref={columnVisibilityRef}>
                    <IconMenuTableControl
                        className="bx bx-columns"
                        onClick={() => setOpenMenuColumns(!openMenuColumns)}
                    />
                    <DivSearchDropdown className={openMenuColumns ? 'active' : 'inactive'}>
                        {table.getAllColumns().map(column => (
                            <DivContainerColumnRow key={column.id}>
                                <label>
                                    <input
                                        checked={column.getIsVisible()}
                                        disabled={!column.getCanHide()}
                                        onChange={column.getToggleVisibilityHandler()}
                                        type="checkbox"
                                    />
                                    {column.columnDef.header}
                                </label>
                            </DivContainerColumnRow>
                        ))}
                    </DivSearchDropdown>
                </DivColumnVisibility>
                <DivPageVisibility>
                    <ButtonChangePage onClick={prevPage} disabled={!canPrev}>
                        <IconArrow className='bx bxs-chevron-left' />
                    </ButtonChangePage>
                    <span>
                        <div>Page</div>
                        <strong>{currentPage} / {totalPages}</strong>
                    </span>
                    <ButtonChangePage onClick={nextPage} disabled={!canNext}>
                        <IconArrow className='bx bxs-chevron-right' />
                    </ButtonChangePage>
                    <SelectShowElement
                        value={pageSize}
                        onChange={e => changePageSize(Number(e.target.value))}
                    >
                        {[20, 50, 100].map(size => (
                            <option key={size} value={size}>Show {size}</option>
                        ))}
                    </SelectShowElement>
                </DivPageVisibility>
            </TableControl>

            <DivTableWrapper>
                {isPageLoading && (
                    <DivLoader>
                        <DivSpinner />
                    </DivLoader>
                )}
                <TableGroups
                    style={{
                        tableLayout: 'fixed',
                        width: table.getTotalSize(),
                    }}
                >
                    <thead>
                        {table.getHeaderGroups().map(headerEl => (
                            <tr key={headerEl.id}>
                                {headerEl.headers.map(columnEl => (
                                    <th
                                        key={columnEl.id}
                                        colSpan={columnEl.colSpan}
                                        style={{ width: columnEl.getSize() }}
                                    >
                                        <div className='header-name'>
                                            <span>
                                                {flexRender(columnEl.column.columnDef.header, columnEl.getContext())}
                                            </span>
                                            {columnEl.column.id !== 'details' &&
                                                columnEl.column.getCanSort() &&
                                                getSortOrder(columnEl.column.getIsSorted(), columnEl)}
                                        </div>
                                        <div
                                            onMouseDown={columnEl.getResizeHandler()}
                                            onTouchStart={columnEl.getResizeHandler()}
                                            className={`resizer ${columnEl.column.getIsResizing() ? 'isResizing' : ''}`}
                                        />
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => {
                            const isDeleted = row.getVisibleCells().some(
                                cell => cell.column.id === 'isDeleted' && cell.getValue() === true
                            );
                            return (
                                <tr key={row.id} className={isDeleted ? 'isDeleted' : undefined}>
                                    {row.getVisibleCells().map(cell => {
                                        if (cell.column.id === 'details') {
                                            return (
                                                <td key={cell.id} className={cell.column.id}>
                                                    <IconDetail
                                                        className='bx bx-detail'
                                                        onClick={() => detailPage(row.original)}
                                                        ref={el => (buttonOpenDetailPageRef.current[row.id] = el)}
                                                    />
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={cell.id} className={cell.column.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </TableGroups>
            </DivTableWrapper>
        </>
    );
}